/**
 * Airport API
 *
 * RTK Query endpoints for airport autocomplete and lookup operations
 */

import { baseApi } from './baseApi';

export interface AirportAutocomplete {
  iataAirportCode: string;
  utcVariant: string;
  cityName: string;
  airportName: string;
  countryCode: string;
}

export interface Airport {
  _id: string;
  iataAirportCode: string;
  icaoAirportCode: string;
  airportName: string;
  airportShortName: string;
  cityName: string;
  countryCode: string;
  utcVariant: string;
  translations: Record<string, string>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AirportDetail = Airport;

export const airportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Autocomplete endpoint - Optimized for dropdowns
     * Returns lightweight data (IATA code + UTC variation only)
     */
    autocompleteAirports: builder.query<AirportAutocomplete[], { query: string; limit?: number }>({
      query: ({ query }) =>
        `/airports?search=${encodeURIComponent(query)}`,
      // Only cache for 5 minutes for autocomplete results
      keepUnusedDataFor: 300,
    }),

    /**
     * Get specific airport details by IATA code
     */
    getAirportByCode: builder.query<Airport, string>({
      query: (iataCode) => `/airports/${iataCode}`,
      // Cache airport details longer (1 hour)
      keepUnusedDataFor: 3600,
    }),

    /**
     * Get UTC variation only (lightweight endpoint)
     */
    getAirportUtcVariation: builder.query<{ iataCode: string; utcVariation: string }, string>({
      query: (iataCode) => `/airports/${iataCode}/utc-variation`,
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useAutocompleteAirportsQuery,
  useLazyAutocompleteAirportsQuery,
  useGetAirportByCodeQuery,
  useLazyGetAirportByCodeQuery,
  useGetAirportUtcVariationQuery,
} = airportApi;
